/**
 * Demo cloud VMs (?mock=1): a connected AWS account, one VM still starting (no machine yet), one
 * online on the demo Linux build box, one stopped. No real account, role or instance.
 */
import { AWS_DEFAULT_REGION, CLOUD_IDLE_LIMIT_MS, CLOUD_MAX_LIFETIME_MS, awsHourlyUsd } from '@orgus/protocol';
import type { CloudConnection, CloudInstanceCandidate, CloudState, CloudVm, Machine } from '@orgus/protocol';

const MIN = 60_000;

export const MOCK_AWS_CONNECTION = (now: number): CloudConnection => ({
  provider: 'aws',
  state: 'connected',
  externalId: 'demo-external-id-7Hq2Lm9xV4bN1cR8',
  workspaceId: 'org_demo',
  principalArn: 'arn:aws:iam::111122223333:user/orgus-provisioner',
  roleArn: 'arn:aws:iam::444455556666:role/orgus-org_demo',
  accountId: '444455556666',
  region: AWS_DEFAULT_REGION,
  quickCreateUrl: 'https://us-east-1.console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate',
  connectedBy: 'you',
  connectedAt: now - 3 * 24 * 60 * MIN,
  test: { at: now - 2 * 60 * MIN, ok: true, message: 'Orgus can act in AWS account 444455556666.' },
});

export function mockVm(o: Partial<CloudVm> & Pick<CloudVm, 'id' | 'name'>, now: number): CloudVm {
  const size = o.size ?? 't3.medium';
  const region = o.region ?? AWS_DEFAULT_REGION;
  return {
    provider: 'aws',
    instanceId: `i-0${o.id.replace(/\W/g, '').padEnd(16, '0').slice(0, 16)}`,
    region,
    size,
    lifetime: 'ephemeral',
    state: 'running',
    machineId: null,
    repoUrl: null,
    createdBy: 'you',
    createdAt: now - 40 * MIN,
    idleLimitMs: CLOUD_IDLE_LIMIT_MS,
    maxLifetimeMs: CLOUD_MAX_LIFETIME_MS,
    idleStopMs: null,
    idleSince: null,
    op: null,
    hourlyUsd: awsHourlyUsd(size, region),
    error: null,
    updatedAt: now,
    ...o,
  };
}

/** A demo VM's machine: Linux on AWS, online or (stopped) offline, on the demo's newest agent. */
export function mockVmMachine(o: { id: string; name: string; cpus: number; memGib: number; online: boolean; load: number }, like: Machine | undefined, now: number): Machine {
  return {
    ...(like ?? ({} as Machine)),
    id: o.id,
    name: o.name,
    hostname: `ip-172-31-${o.cpus * 10}-${o.memGib}`,
    os: 'linux',
    arch: 'x64',
    cpus: o.cpus,
    memTotal: o.memGib * 1024 ** 3,
    memFree: (o.online ? 0.55 : 0.8) * o.memGib * 1024 ** 3,
    load: o.load,
    online: o.online,
    connectedAt: o.online ? now - 3 * 60 * MIN : null,
    lastSeen: o.online ? now : now - 5 * 60 * MIN,
    labels: [],
    local: false,
  } as Machine;
}

const newest = (machines: Machine[]): Machine | undefined => [...machines].sort((a, b) => (b.agentVersion ?? '').localeCompare(a.agentVersion ?? '', undefined, { numeric: true }))[0];

export function mockCloud(machines: Machine[], now: number): { state: CloudState; extraMachines: Machine[] } {
  const like = newest(machines);
  const online = mockVmMachine({ id: 'm_vm_blazing_tesla', name: 'blazing-tesla', cpus: 2, memGib: 4, online: true, load: 0.18 }, like, now);
  const stopped = mockVmMachine({ id: 'm_vm_kind_curie', name: 'kind-curie', cpus: 2, memGib: 2, online: false, load: 0 }, like, now);
  const vms: CloudVm[] = [
    mockVm({ id: 'vm_demo_start', name: 'cosmic-lovelace', state: 'pending', createdAt: now - 1 * MIN, size: 't3.large' }, now),
    mockVm({ id: 'vm_demo_online', name: online.name, machineId: online.id, idleSince: now - 8 * MIN, createdAt: now - 3 * 60 * MIN }, now),
    mockVm({ id: 'vm_demo_stopped', name: stopped.name, machineId: stopped.id, state: 'stopped', lifetime: 'permanent', size: 't3.small', createdAt: now - 2 * 24 * 60 * MIN }, now),
  ];
  return { state: { connections: [MOCK_AWS_CONNECTION(now)], vms, provisioner: { ready: true, problem: null } }, extraMachines: [online, stopped] };
}

/**
 * ?mock=1&cloud=adopt: three hand-made instances (tag orgus=fleet, stopped) whose agents joined as
 * ip-172-31-* machines, and one untagged instance that runs the agent, for the adopt flow.
 */
export function mockFleet(like: Machine | undefined, now: number): { machines: Machine[]; candidates: CloudInstanceCandidate[] } {
  const machines: Machine[] = [];
  const candidates: CloudInstanceCandidate[] = [];
  for (let n = 1; n <= 4; n++) {
    const host = `ip-172-31-0-${n}`;
    const m = { ...mockVmMachine({ id: `m_fleet_${n}`, name: host, cpus: 2, memGib: 4, online: n === 4, load: n === 4 ? 0.1 : 0 }, like, now), hostname: host };
    machines.push(m);
    const tagged = n !== 4;
    candidates.push({
      instanceId: `i-0f1ee7c1a0de0000${n}`,
      region: AWS_DEFAULT_REGION,
      name: tagged ? `aws-claude-${n}` : 'build-box',
      size: 't3.medium',
      state: tagged ? 'stopped' : 'running',
      privateDns: `${host}.ec2.internal`,
      machineId: m.id,
      machineName: host,
      match: tagged ? ['fleet-tag', 'host-name'] : ['host-name'],
      adoptable: tagged,
      blocked: tagged ? null : `Add the tag orgus=fleet to i-0f1ee7c1a0de0000${n} in the AWS console, then adopt it: Orgus takes over only instances you tagged.`,
    });
  }
  return { machines, candidates };
}
